import { Component } from '@theme/component';

/**
 * Pages the grid in groups of four, preserving a complete grid without JavaScript.
 * @extends {Component<{grid: HTMLUListElement, navigation: HTMLElement, status: HTMLElement}>}
 */
class DSProductGrid extends Component {
  requiredRefs = ['grid', 'navigation', 'status'];
  #page = 0;
  #observer = new MutationObserver(() => this.#render());

  connectedCallback() {
    super.connectedCallback();
    this.#observer.observe(this.refs.grid, { childList: true });
    this.#render();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observer.disconnect();
    this.#observer.observe(this.refs.grid, { childList: true });
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    for (const item of this.#items) item.hidden = false;
    if (this.refs.navigation) this.refs.navigation.hidden = true;
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return Array.from(this.refs.grid?.children ?? []).filter((item) => item instanceof HTMLElement);
  }

  /** Show the preceding group, wrapping to the final group. @param {MouseEvent} _event */
  previous(_event) {
    this.#page -= 1;
    this.#render(true);
  }

  /** Show the next group, wrapping to the first group. @param {MouseEvent} _event */
  next(_event) {
    this.#page += 1;
    this.#render(true);
  }

  /** @param {boolean} [announce] */
  #render(announce = false) {
    const items = this.#items;
    this.dataset.pageItemCount = String(items.length);
    const pages = Math.ceil(items.length / 4);
    this.#page = pages ? ((this.#page % pages) + pages) % pages : 0;
    for (const [index, item] of items.entries()) {
      item.hidden = Math.floor(index / 4) !== this.#page;
    }
    this.refs.navigation.hidden = pages <= 1;
    if (announce) {
      this.refs.status.textContent = (this.dataset.statusTemplate || '')
        .replace('[index]', String(this.#page + 1))
        .replace('[length]', String(pages));
    }
  }
}

if (!customElements.get('ds-product-grid')) {
  customElements.define('ds-product-grid', DSProductGrid);
}
